import { useCallback, useMemo, useState } from "react"
import { Grid, MenuItem } from "@mui/material"
import { useNavigate } from "react-router"
import { enqueueSnackbar } from "notistack"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { MRT_ColumnDef } from "material-react-table"
import { Survey } from "../merchantSurveyTypes"
import {
    FetchAllSurveysResponse,
    deleteMerchantAdminSurveysItem,
    fetchSurveysList,
    SurveyQuery,
} from "../surveysListApi"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import {
    MERCHANT_ADMIN_CREATE_SURVEY_ITEM,
    MERCHANT_ADMIN_ROUTE,
    SURVEY_ROUTE,
    getEditSurveyRoute,
} from "../../../../../constants/routes"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { Period } from "../../../../dashboards/dashboardTypes"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { DestructiveMenuItem } from "../../../../../shared/components/menus/DestructiveMenuItem"

const selectSurveys = (data: FetchAllSurveysResponse) => data?.surveys || []

export function SurveysList() {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>({
        showConfirmModal: false,
        confirmModalText: "",
        itemId: null,
    })

    const surveysResult = useApiQuery(fetchSurveysList, {
        queryName: SurveyQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectSurveys,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(surveysResult)

    const columns = useMemo<MRT_ColumnDef<Survey>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("surveys-list-survey-table-name-column"),
                accessorKey: "title",
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("surveys-list-survey-table-start-date-column"),
                    accessorKey: "activePeriod.from.date",
                },
                (row) => row?.activePeriod?.from?.date,
            ),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("surveys-list-survey-table-end-date-column"),
                    accessorKey: "activePeriod.to.date",
                },
                (row) => row?.activePeriod?.to?.date,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("status"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const { mutate: callDeleteSurveysItem } = useApiMutation(deleteMerchantAdminSurveysItem, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SurveyQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message:
                    data.serviceResponse.displayMessage ?? l10n.getString("surveys-table-list-delete-success-message"),
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteSurveysItem({ surveyId: confirmModalState.itemId })
        } else {
            throw new Error("Surveys id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteSurveysItem])

    const handleShowRatings = (surveyId: number) => {
        const url = `${SURVEY_ROUTE}?surveyId=${surveyId}&period=${Period.LAST_MONTH}`
        navigate(url)
    }

    return (
        <>
            <LocalizedStrict id="surveys-list-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SURVEYS_LIST}
                    title="Surveys"
                    rightElement={
                        <LocalizedStrict id="surveys-list-create-button">
                            <StandardButton onClick={() => navigate(MERCHANT_ADMIN_CREATE_SURVEY_ITEM)}>
                                Create survey
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-communication-header">
                            <Breadcrumb>Communication</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-surveys">
                            <Breadcrumb>Surveys</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <MenuItem
                            onClick={() => {
                                handleShowRatings(row.original.id)
                            }}
                            key={`show-ratings-${row.original.id}`}
                        >
                            {l10n.getString("surveys-table-list-show-ratings")}
                        </MenuItem>,
                        <MenuItem
                            onClick={() => {
                                navigate(getEditSurveyRoute(row.original.id))
                            }}
                            key={`edit-${row.original.id}`}
                        >
                            {l10n.getString("edit")}
                        </MenuItem>,
                        <DestructiveMenuItem
                            onClick={() => {
                                setConfirmModalState({
                                    showConfirmModal: true,
                                    confirmModalText: `${row.original.title}`,
                                    itemId: row.original.id,
                                })
                                closeMenu()
                            }}
                            key={`delete-${row.original.id}`}
                        >
                            {l10n.getString("delete")}
                        </DestructiveMenuItem>,
                    ]}
                />
            </Grid>
            {confirmModalState.showConfirmModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("surveys-table-list-delete-modal-title")}
                    entityName={confirmModalState.confirmModalText}
                    handleClick={handleDeleteClick}
                    open={confirmModalState.showConfirmModal}
                    onClose={() =>
                        setConfirmModalState({
                            showConfirmModal: false,
                            confirmModalText: "",
                            itemId: null,
                        })
                    }
                />
            )}
        </>
    )
}
